import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  MessageSquare, 
  Send, 
  AtSign, 
  Loader2, 
  Clock, 
  User, 
  Building2, 
  Briefcase, 
  Truck,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  X,
  ChevronDown,
  Reply
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// User type badge colors
const userTypeBadges = {
  admin: "bg-purple-100 text-purple-700",
  hospital: "bg-blue-100 text-blue-700",
  contractor: "bg-green-100 text-green-700",
  supplier: "bg-amber-100 text-amber-700"
};

const userTypeIcons = {
  admin: User,
  hospital: Building2,
  contractor: Briefcase,
  supplier: Truck
};

export function ProjectStatusUpdates({ projectId, projectName }) {
  const { token, user } = useAuth();
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [taggableUsers, setTaggableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewUpdateDialog, setShowNewUpdateDialog] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearch, setTagSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  
  const [newUpdate, setNewUpdate] = useState({
    content: "",
    status_change: "",
    tagged_users: [],
    is_status_request: false
  });
  
  const [replyContent, setReplyContent] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (projectId) {
      fetchStatusUpdates();
      fetchTaggableUsers();
    }
  }, [projectId, token]);

  const fetchStatusUpdates = async () => {
    try {
      const res = await fetch(`${API}/api/projects/${projectId}/status-updates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStatusUpdates(data.status_updates || []);
      }
    } catch (error) {
      console.error("Failed to fetch status updates:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaggableUsers = async () => {
    try {
      const res = await fetch(`${API}/api/projects/${projectId}/taggable-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTaggableUsers(data.taggable_users || []);
      }
    } catch (error) {
      console.error("Failed to fetch taggable users:", error);
    }
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    if (!newUpdate.content.trim()) {
      toast.error("Please enter a message");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/projects/${projectId}/status-update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newUpdate.content,
          status_change: newUpdate.status_change || null,
          tagged_users: newUpdate.tagged_users,
          is_status_request: newUpdate.is_status_request
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(
          newUpdate.is_status_request 
            ? `Status request sent to ${data.notifications_sent} user(s)` 
            : "Status update posted successfully"
        );
        setShowNewUpdateDialog(false);
        setNewUpdate({ content: "", status_change: "", tagged_users: [], is_status_request: false });
        fetchStatusUpdates();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to post update");
      }
    } catch (error) {
      toast.error("Failed to post update");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (updateId) => {
    if (!replyContent.trim()) {
      toast.error("Please enter a reply");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/projects/${projectId}/status-update/${updateId}/respond`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          update_id: updateId,
          content: replyContent
        })
      });

      if (res.ok) {
        toast.success("Reply sent successfully");
        setReplyingTo(null);
        setReplyContent("");
        fetchStatusUpdates();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to send reply");
      }
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSubmitting(false);
    }
  };

  const addTag = (userId) => {
    if (!newUpdate.tagged_users.includes(userId)) {
      setNewUpdate(prev => ({
        ...prev,
        tagged_users: [...prev.tagged_users, userId]
      }));
    }
    setShowTagDropdown(false);
    setTagSearch("");
  };

  const removeTag = (userId) => {
    setNewUpdate(prev => ({
      ...prev,
      tagged_users: prev.tagged_users.filter(id => id !== userId)
    }));
  };

  const filteredTaggableUsers = taggableUsers.filter(u => 
    u.label.toLowerCase().includes(tagSearch.toLowerCase()) ||
    u.email?.toLowerCase().includes(tagSearch.toLowerCase())
  );

  const getTaggedUserLabel = (userId) => {
    const user = taggableUsers.find(u => u.id === userId);
    return user?.label || userId;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-600" />
          Status Updates & Activity
        </h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setNewUpdate(prev => ({ ...prev, is_status_request: true }));
              setShowNewUpdateDialog(true);
            }}
            className="text-amber-600 border-amber-300 hover:bg-amber-50"
            data-testid="request-status-btn"
          >
            <HelpCircle className="w-4 h-4 mr-1" />
            Request Status
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setNewUpdate(prev => ({ ...prev, is_status_request: false }));
              setShowNewUpdateDialog(true);
            }}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="add-update-btn"
          >
            <Send className="w-4 h-4 mr-1" />
            Add Update
          </Button>
        </div>
      </div>

      {/* Status Updates Timeline */}
      <div className="space-y-4">
        {statusUpdates.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No status updates yet</p>
            <p className="text-sm text-slate-400">Be the first to add an update or request status</p>
          </div>
        ) : (
          statusUpdates.map((update) => (
            <div 
              key={update.id} 
              className={`bg-white rounded-xl border ${update.is_status_request ? 'border-amber-200' : 'border-slate-200'} p-4 shadow-sm`}
              data-testid={`status-update-${update.id}`}
            >
              {/* Update Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    update.is_status_request ? 'bg-amber-100' : 'bg-blue-100'
                  }`}>
                    {update.is_status_request ? (
                      <HelpCircle className="w-5 h-5 text-amber-600" />
                    ) : (
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{update.created_by_name}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Clock className="w-3 h-3" />
                      {formatDate(update.created_at)}
                      {update.is_status_request && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                          Status Request
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                {update.status_change && (
                  <Badge className="bg-green-100 text-green-700">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Status: {update.status_change}
                  </Badge>
                )}
              </div>

              {/* Update Content */}
              <div className="text-slate-700 mb-3 whitespace-pre-wrap">
                {update.content}
              </div>

              {/* Tagged Users */}
              {update.tagged_user_details?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="text-xs text-slate-500 flex items-center">
                    <AtSign className="w-3 h-3 mr-1" /> Tagged:
                  </span>
                  {update.tagged_user_details.map((taggedUser, idx) => {
                    const IconComponent = userTypeIcons[taggedUser.type] || User;
                    return (
                      <Badge 
                        key={idx} 
                        variant="outline" 
                        className={`${userTypeBadges[taggedUser.type] || 'bg-slate-100 text-slate-700'} text-xs`}
                      >
                        <IconComponent className="w-3 h-3 mr-1" />
                        {taggedUser.name}
                      </Badge>
                    );
                  })}
                </div>
              )}

              {/* Responses */}
              {update.responses?.length > 0 && (
                <div className="mt-4 pl-4 border-l-2 border-slate-200 space-y-3">
                  <p className="text-xs font-medium text-slate-500 uppercase">Responses ({update.responses.length})</p>
                  {update.responses.map((response, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center">
                          <User className="w-3 h-3 text-slate-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-700">{response.created_by_name}</span>
                        <span className="text-xs text-slate-400">{formatDate(response.created_at)}</span>
                      </div>
                      <p className="text-sm text-slate-600">{response.content}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Button */}
              <div className="mt-3 pt-3 border-t border-slate-100">
                {replyingTo === update.id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Write your reply..."
                      className="min-h-[80px] border-slate-200"
                      data-testid={`reply-textarea-${update.id}`}
                    />
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => { setReplyingTo(null); setReplyContent(""); }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => handleSubmitReply(update.id)}
                        disabled={submitting}
                        className="bg-blue-700 hover:bg-blue-800"
                        data-testid={`submit-reply-btn-${update.id}`}
                      >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                        Send Reply
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setReplyingTo(update.id)}
                    className="text-slate-500 hover:text-blue-600"
                    data-testid={`reply-btn-${update.id}`}
                  >
                    <Reply className="w-4 h-4 mr-1" />
                    Reply
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* New Update Dialog */}
      <Dialog open={showNewUpdateDialog} onOpenChange={setShowNewUpdateDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {newUpdate.is_status_request ? (
                <>
                  <HelpCircle className="w-5 h-5 text-amber-600" />
                  Request Status Update
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 text-blue-600" />
                  Add Status Update
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitUpdate} className="space-y-4">
            {/* Message */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                {newUpdate.is_status_request ? "Request Message" : "Update Message"}
              </label>
              <Textarea
                ref={textareaRef}
                value={newUpdate.content}
                onChange={(e) => setNewUpdate(prev => ({ ...prev, content: e.target.value }))}
                placeholder={newUpdate.is_status_request 
                  ? "What status information do you need?" 
                  : "Share your update here..."
                }
                className="min-h-[120px] border-slate-200"
                required
                data-testid="update-content-textarea"
              />
            </div>

            {/* Tag Users */}
            <div className="relative">
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Tag Users
                <span className="text-xs text-slate-400 ml-2">(will receive email notification)</span>
              </label>
              
              {/* Selected Tags */}
              {newUpdate.tagged_users.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {newUpdate.tagged_users.map(userId => (
                    <Badge key={userId} variant="secondary" className="flex items-center gap-1">
                      <AtSign className="w-3 h-3" />
                      {getTaggedUserLabel(userId)}
                      <button 
                        type="button"
                        onClick={() => removeTag(userId)}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Tag Search Input */}
              <div className="relative">
                <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={tagSearch}
                  onChange={(e) => { setTagSearch(e.target.value); setShowTagDropdown(true); }}
                  onFocus={() => setShowTagDropdown(true)}
                  placeholder="Search users to tag..."
                  className="pl-9 border-slate-200"
                  data-testid="tag-search-input"
                />
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>

              {/* Dropdown */}
              {showTagDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredTaggableUsers.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-slate-500">No users found</div>
                  ) : (
                    filteredTaggableUsers.map(user => {
                      const IconComponent = userTypeIcons[user.type] || User;
                      const isSelected = newUpdate.tagged_users.includes(user.id);
                      return (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => addTag(user.id)}
                          disabled={isSelected}
                          className={`w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-3 ${
                            isSelected ? 'opacity-50 cursor-not-allowed bg-slate-50' : ''
                          }`}
                          data-testid={`tag-user-${user.id}`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${userTypeBadges[user.type]}`}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{user.label}</p>
                            <p className="text-xs text-slate-500 truncate">{user.email}</p>
                          </div>
                          {isSelected && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Status Change (optional) */}
            {!newUpdate.is_status_request && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Update Project Status (optional)
                </label>
                <Select
                  value={newUpdate.status_change}
                  onValueChange={(value) => setNewUpdate(prev => ({ ...prev, status_change: value }))}
                >
                  <SelectTrigger className="border-slate-200" data-testid="status-change-select">
                    <SelectValue placeholder="No status change" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No status change</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowNewUpdateDialog(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || !newUpdate.content.trim()}
                className={newUpdate.is_status_request 
                  ? "bg-amber-600 hover:bg-amber-700 text-white"
                  : "bg-blue-700 hover:bg-blue-800 text-white"
                }
                data-testid="submit-update-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {newUpdate.is_status_request ? "Send Request" : "Post Update"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProjectStatusUpdates;
